/**
 * Financial Engine — Amwāl OS
 *
 * Macro-level financial safety tracking for every entity under the holding:
 *  - Holding-wide roll-up (consolidated cash, burn, runway, warnings).
 *  - Per-entity runway cards with color-coded status (green/amber/red).
 *  - Warnings panel surfacing 1-year-ahead alerts (runway<12mo, burn
 *    acceleration, revenue decline, stale snapshot, negative cash).
 *  - Per-entity detail drawer with full snapshot history + entry form +
 *    CSV import + per-entity threshold overrides + 12-month forecast.
 *  - Reports view with weekly / monthly / quarterly / annual cadences and
 *    optional AI commentary per entity row.
 *
 * Access: holding_admin / system_admin / ceo / cfo see everything in their
 * tenant. Founders see only entities where they are in `assigned_user_ids`.
 *
 * Layout: this file owns the page header, holding roll-up, status bar,
 * entity-grid, and warnings panel. Heavier sub-views live in dedicated
 * files under `/components/finance/`:
 *   - FinanceEntityModal     — "New entity" creation
 *   - FinanceEntityDrawer    — slide-in per-entity detail panel
 *   - FinanceReportsView     — periodic reports (W/M/Q/A) + AI commentary
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Wallet, TrendingDown, AlertCircle, AlertTriangle, Info, Plus,
  Building2, RefreshCw, ChevronRight, BarChart3, Check, ArrowRightLeft,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { usePermissions, P } from '@/lib/permissions';
import useTenantSocket from '@/lib/useTenantSocket';
import {
  REPORTING_CURRENCIES, STATUS_META, SEVERITY_META, fmtCurrency, fmtMonths,
} from '@/components/finance/financeUtils';
import FinanceEntityModal from '@/components/finance/FinanceEntityModal';
import FinanceEntityDrawer from '@/components/finance/FinanceEntityDrawer';
import FinanceReportsView from '@/components/finance/FinanceReportsView';

// ────────────────────────────────────────────────────────────────────────
//  HOLDING ROLL-UP STRIP
// ────────────────────────────────────────────────────────────────────────
function RollupStrip({ rollup }) {
  if (!rollup) return null;
  const cur = rollup.reporting_currency || 'QAR';
  const tiles = [
    { label: 'Entities tracked', value: rollup.entity_count, icon: Building2 },
    {
      label: 'Consolidated cash',
      value: fmtCurrency(rollup.total_cash, cur),
      icon: Wallet,
      hint: rollup.fx_normalised ? `FX-normalised to ${cur}` : 'Naive sum (FX unavailable)',
    },
    { label: 'Total monthly burn', value: fmtCurrency(rollup.total_avg_burn_3mo, cur), icon: TrendingDown, hint: '3-mo trailing avg' },
    {
      label: 'Consolidated runway',
      value: fmtMonths(rollup.consolidated_runway_months),
      icon: BarChart3,
      hint: rollup.consolidated_runway_months ? null : 'Cash-flow positive or no burn data',
    },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="finance-rollup-strip">
      {tiles.map((t, i) => (
        <div key={i} className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-wider text-slate-500">{t.label}</span>
            <t.icon className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-semibold text-slate-900 tabular-nums">{t.value}</div>
          {t.hint && <div className="text-[11px] text-slate-400 mt-1">{t.hint}</div>}
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
//  STATUS COUNTS BAR
// ────────────────────────────────────────────────────────────────────────
function StatusCountsBar({ statusCounts, weakest }) {
  if (!statusCounts) return null;
  const total = Object.values(statusCounts).reduce((a, b) => a + b, 0) || 1;
  const ordered = ['red', 'amber', 'green', 'infinite', 'unknown'];
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wider text-slate-500">Portfolio runway distribution</div>
        {weakest && (
          <div className="text-xs text-slate-600" data-testid="finance-weakest-entity">
            Weakest: <span className="font-semibold text-slate-900">{weakest.name}</span>{' '}
            <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
              style={{ backgroundColor: STATUS_META[weakest.status]?.bg, color: STATUS_META[weakest.status]?.text }}>
              {fmtMonths(weakest.runway_months)}
            </span>
          </div>
        )}
      </div>
      <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
        {ordered.map((s) => {
          const count = statusCounts[s] || 0;
          if (count === 0) return null;
          return (
            <div
              key={s}
              style={{ width: `${(count / total) * 100}%`, backgroundColor: STATUS_META[s].accent }}
              title={`${STATUS_META[s].label}: ${count}`}
              data-testid={`finance-status-bar-${s}`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 mt-3">
        {ordered.map((s) => statusCounts[s] > 0 && (
          <div key={s} className="flex items-center gap-1.5 text-xs">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_META[s].accent }} />
            <span className="text-slate-600">{STATUS_META[s].label}</span>
            <span className="font-semibold text-slate-900">{statusCounts[s]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
//  ENTITY CARD
// ────────────────────────────────────────────────────────────────────────
function EntityCard({ entity, onOpen }) {
  const rw = entity.runway || {};
  const meta = STATUS_META[rw.runway_status] || STATUS_META.unknown;
  const Icon = meta.icon;
  const ai = entity.commentary;
  return (
    <button
      onClick={() => onOpen(entity.entity_id)}
      data-testid={`finance-entity-card-${entity.entity_id}`}
      className="text-left bg-white border border-slate-200 rounded-2xl p-5 hover:border-[#8B3FC5] hover:shadow-sm transition-all relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 h-1 w-full" style={{ backgroundColor: meta.accent }} />
      <div className="flex items-start justify-between mb-4 mt-1">
        <div>
          <div className="text-base font-semibold text-slate-900">{entity.name}</div>
          <div className="text-xs text-slate-500 mt-1">
            {entity.sector || 'Uncategorised'} · {entity.currency || 'QAR'} · {entity.status || 'active'}
          </div>
        </div>
        <span
          className="px-2 py-1 rounded-full text-[10px] font-semibold flex items-center gap-1"
          style={{ backgroundColor: meta.bg, color: meta.text }}
        >
          <Icon className="w-3 h-3" />
          {meta.label}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="text-slate-500 uppercase tracking-wider mb-0.5">Cash</div>
          <div className="text-slate-900 font-semibold tabular-nums">{fmtCurrency(rw.cash_position, entity.currency)}</div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-wider mb-0.5">Runway</div>
          <div className="font-semibold tabular-nums" style={{ color: meta.accent }}>
            {fmtMonths(rw.runway_months)}
          </div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-wider mb-0.5">Burn (3-mo avg)</div>
          <div className="text-slate-900 font-semibold tabular-nums">
            {fmtCurrency(rw.avg_monthly_burn_3mo, entity.currency)}
          </div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-wider mb-0.5">Snapshots</div>
          <div className="text-slate-900 font-semibold tabular-nums">{rw.snapshot_count || 0}</div>
        </div>
      </div>
      {ai && (
        <div
          className="mt-4 pt-3 border-t border-[#8B3FC5]/30 flex items-start gap-2"
          data-testid={`finance-entity-card-commentary-${entity.entity_id}`}
        >
          <Sparkles className="w-3.5 h-3.5 mt-0.5 text-[#5E1F8B] shrink-0" />
          <span className="text-[12px] italic text-slate-700 leading-snug">{ai}</span>
        </div>
      )}
      <div className="flex items-center justify-end gap-1 mt-4 text-xs text-slate-400 group-hover:text-[#8B3FC5]">
        Open detail <ChevronRight className="w-3 h-3" />
      </div>
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────
//  WARNINGS PANEL
// ────────────────────────────────────────────────────────────────────────
function WarningsPanel({ warnings, counts, onAck, onOpenEntity }) {
  if (!warnings || warnings.length === 0) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center" data-testid="finance-warnings-empty">
        <Check className="w-5 h-5 text-emerald-600 mx-auto mb-2" />
        <div className="text-sm font-semibold text-emerald-900">No active warnings.</div>
        <div className="text-xs text-emerald-700 mt-1">All entities are above the warning thresholds.</div>
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="finance-warnings-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-sm font-semibold text-slate-900">1-year-ahead warnings</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {counts.critical} critical · {counts.warning} warning · {counts.info} info
          </div>
        </div>
      </div>
      <div className="space-y-2 max-h-[420px] overflow-y-auto">
        {warnings.map((w) => {
          const meta = SEVERITY_META[w.severity] || SEVERITY_META.info;
          const Icon = meta.icon;
          return (
            <div
              key={w.warning_id}
              className="rounded-xl border p-3 flex items-start gap-3"
              style={{ backgroundColor: meta.bg, borderColor: meta.accent + '40' }}
              data-testid={`finance-warning-${w.warning_id}`}
            >
              <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: meta.accent }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] uppercase font-semibold tracking-wider" style={{ color: meta.text }}>
                    {meta.label}
                  </span>
                  <span className="text-[10px] text-slate-400">·</span>
                  <span className="text-[10px] text-slate-500">{w.kind.replace(/_/g, ' ')}</span>
                </div>
                <div className="text-sm text-slate-800">{w.message}</div>
              </div>
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => onOpenEntity(w.entity_id)}
                  className="text-[11px] text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline"
                  data-testid={`finance-warning-open-${w.warning_id}`}
                >
                  Open
                </button>
                <button
                  onClick={() => onAck(w.warning_id)}
                  className="text-[11px] text-slate-500 hover:text-slate-900"
                  data-testid={`finance-warning-ack-${w.warning_id}`}
                >
                  Dismiss
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
//  MAIN PAGE
// ────────────────────────────────────────────────────────────────────────
/* eslint-disable -- v6 react-hooks/immutability + preserve-manual-memoization false-positives on useMemo objects. */
export default function FinanceEngine() {
  const { can } = usePermissions();
  const [overview, setOverview] = useState(null);
  const [warnings, setWarnings] = useState({ warnings: [], counts: { critical: 0, warning: 0, info: 0 } });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [openEntityId, setOpenEntityId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [view, setView] = useState('overview');  // 'overview' | 'reports'
  const [tenantDefaultCurrency, setTenantDefaultCurrency] = useState(null);
  const [overviewAiOn, setOverviewAiOn] = useState(() => {
    try { return localStorage.getItem('ve_finance_overview_ai_on') !== '0'; } catch { return true; }
  });
  const [reportingCurrency, setReportingCurrency] = useState(() => {
    try { return localStorage.getItem('ve_finance_reporting_currency') || 'QAR'; } catch { return 'QAR'; }
  });

  // Hydrate the picker from the tenant's saved default the first time it
  // loads, but only if the user hasn't already overridden it locally this
  // session. This makes the picker "remember" the admin's choice across
  // browsers + new users.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/tenants/me');
        if (data?.reporting_currency) {
          setTenantDefaultCurrency(data.reporting_currency);
          const hasLocal = (() => { try { return !!localStorage.getItem('ve_finance_reporting_currency'); } catch { return false; } })();
          if (!hasLocal) setReportingCurrency(data.reporting_currency);
        }
      } catch { /* tenant endpoint may 404 on edge cases — UI just stays on default */ }
    })();
  }, []);

  const onChangeCurrency = (cur) => {
    setReportingCurrency(cur);
    try { localStorage.setItem('ve_finance_reporting_currency', cur); } catch { /* ignore */ }
  };

  const toggleOverviewAi = () => {
    setOverviewAiOn((prev) => {
      const next = !prev;
      try { localStorage.setItem('ve_finance_overview_ai_on', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  // Live broadcast — when ANOTHER admin (CFO/CEO on a different tab or
  // browser) flips the tenant's default reporting currency, OR a finance
  // event (snapshot recorded, warning fired, recompute cron, entity
  // created/deleted) lands on this tenant, refresh the relevant slice of
  // the UI in real-time. Warnings events are debounced so a recompute
  // sweep across N entities triggers a single reload, not N.
  const reloadDebounceRef = React.useRef(null);
  useTenantSocket((msg) => {
    if (!msg || !msg.type) return;
    if (msg.type === 'tenant.reporting_currency') {
      const newCur = msg.reporting_currency;
      if (!newCur) return;
      setTenantDefaultCurrency(newCur);
      let hasLocal = false;
      try { hasLocal = !!localStorage.getItem('ve_finance_reporting_currency'); } catch { /* ignore */ }
      if (!hasLocal && newCur !== reportingCurrency) {
        setReportingCurrency(newCur);
        toast.info(`Tenant default switched to ${newCur} (by ${msg.changed_by || 'an admin'}).`);
      } else if (hasLocal && newCur !== reportingCurrency) {
        toast.info(`Tenant default is now ${newCur} — your local override (${reportingCurrency}) is unchanged.`);
      }
      return;
    }
    if (msg.type === 'finance.warnings.changed') {
      // Debounce: collapse bursts (e.g. weekly cron looping N entities)
      // into a single reload, ~600ms after the last broadcast lands.
      if (reloadDebounceRef.current) clearTimeout(reloadDebounceRef.current);
      reloadDebounceRef.current = setTimeout(() => {
        reloadDebounceRef.current = null;
        load();
        if (msg.reason === 'scheduled_recompute') {
          toast.info('Weekly warnings refresh just ran — panel updated.');
        } else if (msg.reason === 'warning_acknowledged' && msg.acknowledged_by) {
          // Only show toast if someone ELSE dismissed it — the local
          // acknowledge path already toasts on success.
          toast.info(`Warning dismissed by ${msg.acknowledged_by}.`);
        }
      }, 600);
      return;
    }
  });
  // Clean up the debounce timer on unmount
  useEffect(() => () => {
    if (reloadDebounceRef.current) clearTimeout(reloadDebounceRef.current);
  }, []);

  // Save the picker choice as the tenant default. Admin-only.
  const [savingDefault, setSavingDefault] = useState(false);
  const saveAsTenantDefault = async () => {
    setSavingDefault(true);
    try {
      await api.patch('/tenants/me/reporting-currency', { reporting_currency: reportingCurrency });
      setTenantDefaultCurrency(reportingCurrency);
      toast.success(`${reportingCurrency} is now the tenant-wide default.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update tenant default');
    } finally {
      setSavingDefault(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, warningsRes, entitiesRes] = await Promise.all([
        api.get('/finance/holding/overview', {
          params: {
            reporting_currency: reportingCurrency,
            include_commentary: overviewAiOn ? 1 : 0,
          },
        }),
        api.get('/finance/warnings'),
        api.get('/finance/entities'),
      ]);
      setOverview(overviewRes.data);
      setWarnings(warningsRes.data);
      setIsAdmin(entitiesRes.data.is_admin);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load financial data');
    } finally {
      setLoading(false);
    }
  }, [reportingCurrency, overviewAiOn]);

  useEffect(() => { load(); }, [load]);

  const acknowledge = async (warningId) => {
    try {
      await api.post(`/finance/warnings/${warningId}/acknowledge`);
      toast.success('Warning dismissed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to dismiss');
    }
  };

  const recompute = async () => {
    try {
      const { data } = await api.post('/finance/warnings/recompute');
      toast.success(`Recomputed: ${data.created} active warnings across ${data.entities_scanned} entities`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to recompute');
    }
  };

  const onEntityChanged = () => load();

  if (!can(P.VIEW_FINANCE)) {
    return (
      <div className="p-8 text-center text-slate-500" data-testid="finance-no-access">
        You don&apos;t have access to the Financial Engine.
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="finance-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#8B3FC5] mb-1">Amwāl OS</div>
          <h1 className="text-3xl font-bold text-slate-900">Financial Engine</h1>
          <p className="text-sm text-slate-500 mt-1">
            Macro safety tracking for every subsidiary — cash, burn, runway, and 1-year-ahead warnings.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Reporting currency picker */}
          <div className="flex items-center gap-1.5 px-2 py-1.5 border border-slate-200 rounded-lg bg-white" title="All consolidated amounts will be converted to this currency">
            <ArrowRightLeft className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-[10px] uppercase text-slate-500">Report in</span>
            <select
              value={reportingCurrency}
              onChange={(e) => onChangeCurrency(e.target.value)}
              data-testid="finance-currency-picker"
              className="text-sm font-semibold bg-transparent outline-none cursor-pointer"
            >
              {REPORTING_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {/* "Set as tenant default" — admin-only, only shown when the
                picker differs from the saved tenant default. Clicking
                persists this choice for every member of the tenant. */}
            {isAdmin && tenantDefaultCurrency && tenantDefaultCurrency !== reportingCurrency && (
              <button
                onClick={saveAsTenantDefault}
                disabled={savingDefault}
                data-testid="finance-set-default-currency"
                className="text-[10px] uppercase tracking-wider text-[#5E1F8B] hover:text-[#0F1A2D] font-semibold ml-1 disabled:opacity-50"
                title={`Default is ${tenantDefaultCurrency} — click to make ${reportingCurrency} the tenant-wide default`}
              >
                {savingDefault ? 'Saving…' : 'Set default'}
              </button>
            )}
          </div>
          {/* View tabs */}
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setView('overview')}
              data-testid="finance-view-overview"
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                view === 'overview' ? 'bg-white text-slate-900 shadow-sm font-semibold' : 'text-slate-600'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setView('reports')}
              data-testid="finance-view-reports"
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                view === 'reports' ? 'bg-white text-slate-900 shadow-sm font-semibold' : 'text-slate-600'
              }`}
            >
              Reports
            </button>
          </div>
          {isAdmin && view === 'overview' && (
            <>
              <button
                onClick={toggleOverviewAi}
                data-testid="finance-overview-ai-toggle"
                className={`px-3 py-2 text-sm rounded-lg border flex items-center gap-1.5 transition-colors ${
                  overviewAiOn
                    ? 'bg-[#FFF8E5] text-[#0F1A2D] border-[#8B3FC5]'
                    : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                }`}
                title="Toggle Yusuf-as-actor AI footer on each entity card"
              >
                <Sparkles className="w-4 h-4" />
                AI footer {overviewAiOn ? 'on' : 'off'}
              </button>
              <button
                onClick={recompute}
                data-testid="finance-recompute-btn"
                className="px-3 py-2 text-sm border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 flex items-center gap-1.5"
              >
                <RefreshCw className="w-4 h-4" /> Recompute
              </button>
              <button
                onClick={() => setShowCreate(true)}
                data-testid="finance-new-entity-btn"
                className="px-4 py-2 text-sm bg-[#0F1A2D] text-white rounded-lg font-semibold hover:bg-slate-800 flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" /> New entity
              </button>
            </>
          )}
        </div>
      </div>

      {loading && view === 'overview' && (
        <div className="text-sm text-slate-500 text-center py-12" data-testid="finance-loading">Loading…</div>
      )}

      {view === 'reports' && (
        <FinanceReportsView reportingCurrency={reportingCurrency} isAdmin={isAdmin} />
      )}

      {view === 'overview' && !loading && overview && (
        <div className="space-y-6">
          <RollupStrip rollup={overview.rollup} />

          {overview.rollup.entity_count > 0 ? (
            <>
              <StatusCountsBar
                statusCounts={overview.rollup.status_counts}
                weakest={overview.rollup.weakest_entity}
              />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">Entities</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="finance-entities-grid">
                    {overview.entities.map((e) => (
                      <EntityCard
                        key={e.entity_id}
                        entity={e}
                        onOpen={setOpenEntityId}
                      />
                    ))}
                  </div>
                </div>
                <div className="lg:col-span-1">
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">Warnings</div>
                  <WarningsPanel
                    warnings={warnings.warnings}
                    counts={warnings.counts}
                    onAck={acknowledge}
                    onOpenEntity={setOpenEntityId}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center" data-testid="finance-empty">
              <Wallet className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <div className="text-lg font-semibold text-slate-900 mb-2">No entities yet</div>
              <div className="text-sm text-slate-500 mb-4">
                Add your first subsidiary to start tracking cash, burn, and runway.
              </div>
              {isAdmin && (
                <button
                  onClick={() => setShowCreate(true)}
                  data-testid="finance-empty-cta"
                  className="px-4 py-2 bg-[#0F1A2D] text-white rounded-lg text-sm font-semibold hover:bg-slate-800 inline-flex items-center gap-1.5"
                >
                  <Plus className="w-4 h-4" /> Add first entity
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {showCreate && (
        <FinanceEntityModal
          onClose={() => setShowCreate(false)}
          onCreated={(e) => {
            setShowCreate(false);
            load();
            setOpenEntityId(e.entity_id);
          }}
        />
      )}

      {openEntityId && (
        <FinanceEntityDrawer
          entityId={openEntityId}
          isAdmin={isAdmin}
          onClose={() => setOpenEntityId(null)}
          onChanged={onEntityChanged}
        />
      )}
    </div>
  );
}
